import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { ClipboardList, Calendar, CalendarDays, Archive, Users, Wifi, Loader2, X, CheckSquare, FileText, FolderOpen, MessageSquare, UserCheck } from "lucide-react";
import { fetchEvents, fetchMembers, getExportUrl } from "@/lib/api";
import { getWeekKey, getMonthKey, fmtWeekKey, fmtMonthKey } from "@/lib/chartUtils";
import useRealtimeSync from "@/hooks/useRealtimeSync";
import Header from "@/components/Header";
import EntryForm from "@/components/EntryForm";
import EventLog from "@/components/EventLog";
import StatsView from "@/components/StatsView";
import HistoryView from "@/components/HistoryView";
import RosterView from "@/components/RosterView";
import AttendanceView from "@/components/AttendanceView";
import ApplicationsView from "@/components/ApplicationsView";
import ResourcesView from "@/components/ResourcesView";
import ResponsesView from "@/components/ResponsesView";
import VisitorsView from "@/components/VisitorsView";

const TABS = [
  { key: "roster",       label: "Roster",       Icon: Users },
  { key: "visitors",     label: "Visitors",     Icon: UserCheck },
  { key: "attendance",   label: "Attendance",    Icon: CheckSquare },
  { key: "applications", label: "Applications",  Icon: FileText },
  { key: "resources",    label: "Resources",     Icon: FolderOpen },
  { key: "responses",    label: "Responses",     Icon: MessageSquare },
  { key: "log",          label: "Log",           Icon: ClipboardList },
  { key: "weekly",       label: "This Week",     Icon: Calendar },
  { key: "monthly",      label: "This Month",    Icon: CalendarDays },
  { key: "history",      label: "History",        Icon: Archive },
];

export default function ChartTracker() {
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncFlash, setSyncFlash] = useState(false);
  const [banner, setBanner] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [pw, setPw] = useState("");
  const [pwError, setPwError] = useState(false);
  const prevWk = useRef(getWeekKey(new Date()));
  const prevMk = useRef(getMonthKey(new Date()));
  const editingRef = useRef(false);

  // Check if already unlocked this session
  useEffect(() => {
    if (sessionStorage.getItem("chart_unlocked") === "true") setUnlocked(true);
    // Clear all session auth when leaving the tracker
    return () => {
      sessionStorage.removeItem("chart_unlocked");
      sessionStorage.removeItem("chart_admin");
      sessionStorage.removeItem("chart_admin_pin");
    };
  }, []);

  function handleUnlock(e) {
    e.preventDefault();
    if (pw.toLowerCase() === "chart") {
      setUnlocked(true);
      sessionStorage.setItem("chart_unlocked", "true");
      setPwError(false);
    } else {
      setPwError(true);
    }
  }

  // Read tab from URL query param
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const [tab, setTab] = useState(urlTab || "log");

  // When URL changes (e.g. back from profile), sync tab and scroll
  useEffect(() => {
    if (urlTab) {
      setTab(urlTab);
      setTimeout(() => document.getElementById("tab-section")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [urlTab]);

  const loadData = useCallback(async (force = false) => {
    // Skip refresh if user is actively editing (unless forced)
    if (editingRef.current && !force) return;
    try {
      const [evts, mems] = await Promise.all([fetchEvents(), fetchMembers()]);
      setEvents(evts);
      setMembers(mems);
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const setEditing = useCallback((val) => {
    editingRef.current = val;
  }, []);

  // Real-time sync — reload data when another user makes changes
  const wsStatus = useRealtimeSync(useCallback((action) => {
    loadData();
    setSyncFlash(true);
    setTimeout(() => setSyncFlash(false), 1500);
  }, [loadData]));

  useEffect(() => { loadData(true); }, [loadData]);

  // Week/month boundary detection — only triggers state change when period actually changes
  useEffect(() => {
    const t = setInterval(() => {
      const n = new Date();
      const wk = getWeekKey(n);
      const mk = getMonthKey(n);

      if (wk !== prevWk.current) {
        prevWk.current = wk;
        setBanner({ type: "week", label: "New week started!", sub: `Week of ${fmtWeekKey(wk)}` });
        loadData();
        setTimeout(() => setBanner(null), 10000);
      }
      if (mk !== prevMk.current) {
        prevMk.current = mk;
        setBanner({ type: "month", label: "New month started!", sub: fmtMonthKey(mk) });
        loadData();
        setTimeout(() => setBanner(null), 10000);
      }
    }, 1000);
    return () => clearInterval(t);
  }, [loadData]);

  const handleExport = () => {
    window.open(getExportUrl(), "_blank");
  };

  const wk = getWeekKey();
  const mk = getMonthKey();
  const wEvs = events.filter(e => e.weekKey === wk);
  const mEvs = events.filter(e => e.monthKey === mk);

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-[#0B2A47] flex items-center justify-center p-4">
        <form onSubmit={handleUnlock} className="bg-white rounded-2xl p-8 max-w-xs w-full text-center shadow-xl">
          <img src="/icon-dark.png" alt="CHART" className="h-14 mx-auto mb-4" />
          <h2 className="font-heading text-lg font-extrabold text-[#0B2A47] mb-1">Members Only</h2>
          <p className="text-xs text-[#5A7080] mb-5">Enter the group password to access the tracker</p>
          <input
            type="password"
            value={pw}
            onChange={e => { setPw(e.target.value); setPwError(false); }}
            placeholder="Password"
            autoFocus
            className={`w-full px-4 py-3 text-center text-sm font-semibold border-2 rounded-xl outline-none mb-2 ${
              pwError ? "border-red-400 bg-red-50" : "border-[#D5D0C8] focus:border-[#1E7BC4]"
            }`}
          />
          {pwError && <p className="text-xs text-red-600 mb-2">Wrong password</p>}
          <button type="submit" className="w-full py-3 text-sm font-bold text-white bg-[#1E7BC4] hover:bg-[#1769A8] rounded-xl transition-colors">
            Enter
          </button>
        </form>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center" data-testid="loading-state">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-zinc-400 font-body">Loading CHART Networking...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="chart-tracker-app">
      <Header events={events} onExport={handleExport} onRefresh={loadData} onRoster={() => {
        setTab("roster");
        setTimeout(() => document.getElementById("tab-section")?.scrollIntoView({ behavior: "smooth" }), 50);
      }} />

      {/* Week/Month reset banner */}
      {banner && (
        <div
          data-testid="reset-banner"
          className={`sticky top-[57px] z-40 flex items-center gap-3 px-6 py-3 text-white shadow-lg ${
            banner.type === "month" ? "bg-blue-600" : "bg-green-600"
          }`}
        >
          <span className="text-lg">{banner.type === "month" ? "📆" : "📅"}</span>
          <div className="flex-1">
            <p className="text-sm font-extrabold">{banner.label}</p>
            <p className="text-xs opacity-80">{banner.sub} — stats have been reset for the new period</p>
          </div>
          <button
            data-testid="dismiss-reset-banner"
            onClick={() => setBanner(null)}
            className="p-1 rounded-md hover:bg-white/20 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Live sync status bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-2">
        <div data-testid="sync-status" className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold transition-all duration-300 ${
          syncFlash
            ? 'bg-green-100 text-green-700 border border-green-200'
            : wsStatus === 'connected'
              ? 'bg-emerald-50 text-emerald-600 border border-emerald-100'
              : wsStatus === 'polling'
                ? 'bg-blue-50 text-blue-600 border border-blue-100'
                : wsStatus === 'connecting'
                  ? 'bg-amber-50 text-amber-600 border border-amber-100'
                  : 'bg-zinc-50 text-zinc-500 border border-zinc-200'
        }`}>
          {syncFlash ? (
            <><Loader2 className="w-3 h-3 animate-spin" /> Syncing new data...</>
          ) : wsStatus === 'connected' ? (
            <><Wifi className="w-3 h-3" /> Live — real-time sync active</>
          ) : wsStatus === 'polling' ? (
            <><Wifi className="w-3 h-3" /> Auto-refresh active</>
          ) : wsStatus === 'connecting' ? (
            <><Loader2 className="w-3 h-3 animate-spin" /> Connecting...</>
          ) : (
            <><Wifi className="w-3 h-3" /> Auto-refresh active</>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 space-y-4">
        {/* Entry Form */}
        <EntryForm members={members} onEventCreated={loadData} />

        {/* Tabs */}
        <div id="tab-section" className="flex gap-1 flex-wrap" data-testid="tab-bar">
          {TABS.map(t => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md text-xs font-bold transition-all border ${
                tab === t.key
                  ? 'bg-zinc-900 text-white border-zinc-900'
                  : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
              }`}
            >
              <t.Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="bg-white rounded-lg border border-zinc-200 p-5" data-testid="tab-content">
          {tab === "log" && (
            <EventLog events={events} members={members} onDataChanged={loadData} onEditingChange={setEditing} />
          )}
          {tab === "weekly" && (
            <StatsView events={wEvs} members={members} title={`Week of ${fmtWeekKey(wk)}`} />
          )}
          {tab === "monthly" && (
            <StatsView events={mEvs} members={members} title={fmtMonthKey(mk)} />
          )}
          {tab === "history" && (
            <HistoryView events={events} members={members} />
          )}
          {tab === "roster" && (
            <RosterView members={members} events={events} onDataChanged={loadData} />
          )}
          {tab === "visitors" && (
            <VisitorsView events={events} onDataChanged={loadData} />
          )}
          <div style={{ display: tab === "attendance" ? "block" : "none" }}>
            <AttendanceView members={members} />
          </div>
          {tab === "applications" && (
            <ApplicationsView />
          )}
          {tab === "resources" && (
            <ResourcesView members={members} />
          )}
          {tab === "responses" && (
            <ResponsesView members={members} />
          )}
        </div>
      </div>
    </div>
  );
}
